import { mapCommonFields } from './property-mapper-utils';

import type {
  PropertyRegistrationForm,
  FieldStaffItemWithId,
  ContractFactoryItemWithId,
} from '../types/property.types';
import type {
  UpdatePropertyRequest,
  UpdatePropertyFieldStaff,
  UpdatePropertyContractFactory,
  UpdatePropertyReceiptDocument,
} from '../services/properties.service';

/**
 * Transform PropertyRegistrationForm to UpdatePropertyRequest (API format)
 * Maps frontend form field names to backend API field names (camelCase)
 * Includes id preservation for existing items from database
 */
export const mapFormToUpdatePropertyRequest = (
  data: PropertyRegistrationForm,
  canManagePrice: boolean
): UpdatePropertyRequest => {
  // Transform fieldStaffs with displayOrder and optional id
  // id is included only if it's from DB (UUID), omitted for new items (backend will create)
  // Temporary UI ids (starting with 'temp_') are not sent to backend
  const fieldStaffs: UpdatePropertyFieldStaff[] = data.field_staffs.map((staff, index) => {
    const hasDbId =
      'id' in staff && staff.id && typeof staff.id === 'string' && !staff.id.startsWith('temp_');

    return {
      ...(hasDbId ? { id: (staff as FieldStaffItemWithId).id } : {}), // Include id only if from DB
      displayOrder: index + 1, // 1-based index
      name: staff.name,
      phoneNumber: staff.tel, // tel -> phoneNumber
      mailAddress: staff.email, // email -> mailAddress
    };
  });

  // Transform contractFactories with displayOrder and optional id
  // id is included only if it's from DB (UUID), omitted for new items (backend will create)
  // Temporary UI ids (starting with 'temp_') are not sent to backend
  const contractFactories: UpdatePropertyContractFactory[] = data.contract_factories.map(
    (factory, index) => {
      const hasDbId =
        'id' in factory &&
        factory.id &&
        typeof factory.id === 'string' &&
        !factory.id.startsWith('temp_');

      return {
        ...(hasDbId ? { id: (factory as ContractFactoryItemWithId).id } : {}), // Include id only if from DB
        displayOrder: index + 1, // 1-based index
        factoryName: factory.factory, // factory -> factoryName
        deliveryCategory: factory.delivery_classification, // delivery_classification -> deliveryCategory
        quantity: factory.quantity || null,
      };
    }
  );

  // TO DO: Add receiptDocuments handling when form supports it
  const receiptDocuments: UpdatePropertyReceiptDocument[] =
    data.receipt_documents?.map((document) => ({
      id: document.id ?? undefined,
      classification: document.classification ?? 0,
      title: document.title ?? '',
      remarks: document.remarks ?? '',
    })) ?? [];

  // Get common fields from utility
  const commonFields = mapCommonFields(data, canManagePrice);

  // Return combined object with update-specific fields
  return {
    ...commonFields,
    commercialDistributionMap: undefined, // TO DO: Add to form if needed
    fieldStaffs,
    contractFactories,
    receiptDocuments, // TO DO: Implement when form supports it
  };
};
